"use client";

import React, { useRef, useState, useEffect } from "react";
import AvatarCropper from "../cropper/AvatarCropper";
import { getDefaultAvatar } from "../../../../utils/helpers/avatarFallback";
import { Loader } from "../../../common";
import { Pencil } from "lucide-react";
import "./AvatarUploader.css";

// Define accepted shapes and sizes
type AvatarShape = "circle" | "rounded" | "square";
type AvatarSize = "sm" | "md" | "lg";
type AvatarType = "user" | "group";


interface AvatarUploaderProps {
  initialImage?: string;
  shape?: AvatarShape;
  size?: AvatarSize;
  userColor?: string;
  avatarType?: AvatarType;
  onChange: (file: File | null, preview: string | null) => void;
}

const AvatarUploader: React.FC<AvatarUploaderProps> = ({
  initialImage,
  shape = "circle",
  size = "md",
  userColor,
  avatarType = "user",
  onChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [image, setImage] = useState<string | null>(initialImage || null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(!!initialImage);
  const [retryCount, setRetryCount] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);

  const defaultAvatar = getDefaultAvatar(avatarType);
  const finalSrc = loadFailed ? defaultAvatar : (image || defaultAvatar);


  // initialImage action
  useEffect(() => {
    if (initialImage && initialImage !== image) {
      setImage(initialImage);
      setRetryCount(0);
      setLoadFailed(false);
      setIsImageLoading(true);
    }
  }, [initialImage]);

  // Handle image selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(reader.result as string);
      setIsCropping(true);
    };
    reader.readAsDataURL(file);
  };

  // Handle crop completion
  const handleCropDone = (croppedImage: string) => {
    fetch(croppedImage)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
        setImage(croppedImage);
        setRetryCount(0);
        setLoadFailed(false);
        setIsImageLoading(true);
        onChange(file, croppedImage);
        setIsCropping(false);
      });
  };

  // Trigger file input
  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`avatar-uploader ${shape} ${size}`}>
      {isCropping && selectedImage ? (
        <AvatarCropper
          imageSrc={selectedImage}
          shape={shape === "square" ? "rounded" : shape}
          onCropDone={handleCropDone}
          onCancel={() => setIsCropping(false)}
        />
      ) : (
        <div
          className="avatar-wrapper"
          onClick={triggerFileSelect}
          style={{ border: `4px solid ${userColor}` }}
        >
          {isImageLoading &&
            <Loader
              size="sm"
              variant="dots"
              color="primary"
            />
          }
          <img
            src={finalSrc}
            alt="Avatar"
            className="avatar-img"
            onLoad={() => {
              setLoadFailed(false);
              setIsImageLoading(false);
            }}

            onError={() => {
              if (retryCount < 3 && image) {
                setTimeout(() => {
                  setRetryCount((prev) => prev + 1);
                  setIsImageLoading(true);
                }, 1000);
              } else {
                setLoadFailed(true);
                setIsImageLoading(false);
              }
            }}

            style={{ display: isImageLoading ? "none" : "block" }}
          />
          <div className="edit-overlay">
            <Pencil
              size={16}
              className="edit-icon"
              style={{ color: userColor }}
            />
          </div>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageSelect}
            hidden
          />
        </div>
      )}
    </div>
  );
};

export default AvatarUploader;
